/* eslint-disable react/prop-types */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownUp,
  ArrowLeft,
  ArrowRight,
  CircleFadingPlus,
  Clock3,
  ExternalLink,
  Pin,
  PinOff,
  SlidersHorizontal,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  fetchWalletStories,
  handleCollectionImageError,
  toggleSave,
} from "@/lib/wallet";
import { supabase } from "@/lib/supabaseClient";

const FILTERS = ["All", "New", "Pinned"];

function Wallet() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [sort, setSort] = useState("recent");
  const [uid, setUid] = useState(null);

  React.useEffect(() => {
    let alive = true;

    async function load(userId) {
      setLoadError("");
      const stories = await fetchWalletStories(userId);
      if (alive) setProducts(stories);
    }

    (async () => {
      setLoading(true);

      const { data: userRes, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;

      const userId = userRes?.user?.id;
      if (!userId) throw new Error("Not signed in");

      setUid(userId);
      await load(userId);
    })()
      .catch((error) => {
        console.error(error);
        if (alive) {
          setLoadError(error?.message || "Could not load your collection.");
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id;
      if (!userId) return;

      setUid(userId);
      load(userId).catch((error) => {
        console.error(error);
        if (alive) {
          setLoadError(error?.message || "Could not load your collection.");
        }
      });
    });

    return () => {
      alive = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const counts = useMemo(
    () => ({
      All: products.length,
      New: products.filter((product) => product.isNew).length,
      Pinned: products.filter((product) => product.saved).length,
    }),
    [products]
  );

  const filtered = useMemo(() => {
    let list = products;

    if (activeFilter === "New") {
      list = list.filter((product) => product.isNew);
    }

    if (activeFilter === "Pinned") {
      list = list.filter((product) => product.saved);
    }

    return [...list].sort((a, b) => {
      const pinDelta = Number(Boolean(b.saved)) - Number(Boolean(a.saved));
      if (pinDelta !== 0) return pinDelta;

      if (sort === "name") {
        return (a.title ?? "").localeCompare(b.title ?? "");
      }

      const aTime = new Date(a.sortAt || 0).getTime();
      const bTime = new Date(b.sortAt || 0).getTime();
      return bTime - aTime;
    });
  }, [activeFilter, products, sort]);

  async function handleTogglePinned(creationId, nextPinned) {
    setProducts((current) =>
      current.map((item) =>
        item.creationId === creationId ? { ...item, saved: nextPinned } : item
      )
    );

    try {
      await toggleSave(creationId, nextPinned, uid);
    } catch (error) {
      console.error(error);
      setProducts((current) =>
        current.map((item) =>
          item.creationId === creationId
            ? { ...item, saved: !nextPinned }
            : item
        )
      );
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-8 md:pt-12">
      <Button
        asChild
        variant="ghost"
        className="rounded-full px-3 font-sans hover:bg-white/60 hover:text-black"
      >
        <Link to="/members">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to your space
        </Link>
      </Button>

      <header className="mx-auto my-10 max-w-2xl text-center">
        <Badge variant="secondary" className="rounded-full font-medium px-3 font-sans">
          Personal archive
        </Badge>
        <h1 className="mt-4 font-kalice text-5xl font-medium leading-[1.05] md:text-6xl">
          Your collection
        </h1>
        <p className="mt-4 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
          Experiences and pieces you have created with Antoia, held together in
          one private collection.
        </p>
{/*         <Button
          asChild
          variant="outline"
          className="mt-6 rounded-full border-black/10 bg-white/50 font-sans hover:bg-white hover:text-black"
        >
          <Link to="/palaisantoia/trionfi">
            Explore the public Trionfi archive
            <ExternalLink className="ml-2 h-4 w-4" />
          </Link>
        </Button> */}
      </header>

      {products.length > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border-b-2 border-white/20 bg-white/25 p-3 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 overflow-x-auto py-1">
            <p className="flex shrink-0 items-center gap-2 px-2 font-sans text-sm text-black/55">
              <SlidersHorizontal className="h-4 w-4" />
              Filter
            </p>
            {FILTERS.map((filter) => {
              const isActive = activeFilter === filter;

              return (
                <Button
                  key={filter}
                  type="button"
                  onClick={() => setActiveFilter(filter)}
                  variant={isActive ? "default" : "outline"}
                  className={[
                    "h-10 shrink-0 rounded-full px-4 font-sans text-sm",
                    isActive
                      ? "bg-black text-white hover:bg-black/90"
                      : "border-black/10 bg-white/50 hover:bg-white hover:text-black",
                  ].join(" ")}
                >
                  {filter}
                  <span className="ml-2 opacity-60">{counts[filter]}</span>
                </Button>
              );
            })}
          </div>

          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 gap-2 rounded-full border-black/10 bg-white/50 px-4 font-sans text-sm hover:bg-white hover:text-black"
            onClick={() => setSort((current) => (current === "recent" ? "name" : "recent"))}
          >
            <ArrowDownUp className="h-4 w-4" />
            {sort === "recent" ? "Most recent" : "Name"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="mt-6 rounded-3xl border border-black/10 p-10">
          <p className="font-sans text-sm opacity-70">Loading your collection…</p>
        </div>
      ) : loadError ? (
        <div className="mt-6 rounded-3xl border border-red-500/20 p-10">
          <p className="font-sans text-sm text-red-700">{loadError}</p>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          hasCreations={products.length > 0}
          activeFilter={activeFilter}
          onShowAll={() => setActiveFilter("All")}
        />
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((product) => (
            <CollectionCard
              key={product.id}
              creation={product}
              onTogglePinned={handleTogglePinned}
            />
          ))}
        </div>
      )}

      {products.length > 0 && (
        <p className="mt-6 px-2 font-sans text-sm text-muted-foreground">
          Pin the creations you return to most often. They will remain at the top
          of your collection.
        </p>
      )}
    </div>
  );
}

function CollectionCard({ creation, onTogglePinned }) {
  return (
    <article className="group overflow-hidden rounded-3xl border-2 border-white bg-white/50 p-3 backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/75">
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#181313]">
        <Link
          to={creation.route}
          className="block h-full w-full"
          aria-label={`Open ${creation.title}`}
        >
          <img
            src={creation.image}
            alt={creation.title}
            onError={(event) =>
              handleCollectionImageError(event, creation.fallbackImage)
            }
            className={[
              "h-full w-full transition duration-500 group-hover:scale-[1.015]",
              creation.imageFit === "contain" ? "object-cover" : "object-cover",
              creation.trionfi ? "p-0" : "",
            ].join(" ")}
            draggable={false}
          />
        </Link>

        <Button
          type="button"
          variant="secondary"
          className={[
            "absolute right-3 top-3 h-10 w-10 rounded-full border p-0 backdrop-blur",
            creation.saved
              ? "border-black/20 bg-black text-white hover:bg-black/90"
              : "border-white/60 bg-white/85 text-black hover:bg-white",
          ].join(" ")}
          onClick={() =>
            onTogglePinned?.(creation.creationId, !creation.saved)
          }
          aria-label={creation.saved ? "Unpin creation" : "Pin creation"}
          title={creation.saved ? "Unpin creation" : "Pin creation"}
        >
          {creation.saved ? (
            <Pin className="h-4 w-4" />
          ) : (
            <PinOff className="h-4 w-4" />
          )}
        </Button>

        {creation.isNew && (
          <Badge className="absolute left-3 top-3 rounded-full bg-[#ff813d] px-5 py-2 font-medium text-white hover:bg-[#ff813d]">
            New
          </Badge>
        )}
      </div>

      <div className="flex items-end justify-between gap-4 p-3 pb-2 pt-4">
        <div className="min-w-0">
          <p className="font-sans text-xs uppercase tracking-[0.14em] text-black/40">
            {creation.eyebrow}
          </p>
          <Link to={creation.route} className="group/title">
            <h2 className="mt-1 truncate font-kalice text-2xl font-medium group-hover/title:underline group-hover/title:underline-offset-4">
              {creation.title}
            </h2>
          </Link>
          <div className="mt-2 flex items-center gap-2 font-sans text-xs text-black/45">
            <Clock3 className="h-3.5 w-3.5 shrink-0" />
            <span>{creation.date || "Date unavailable"}</span>
            {creation.variant && (
              <>
                <span aria-hidden="true">·</span>
                <span className="truncate">{creation.variant}</span>
              </>
            )}
          </div>
        </div>

        <Button
          asChild
          variant="ghost"
          className="h-10 w-10 shrink-0 rounded-full p-0 hover:bg-black hover:text-white"
        >
          <Link to={creation.route} aria-label={`Open ${creation.title}`}>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </article>
  );
}

function EmptyState({ hasCreations, activeFilter, onShowAll }) {
  return (
    <div className="my-8 rounded-3xl border-2 border-white bg-white/45 p-8 backdrop-blur md:p-10">
      <CircleFadingPlus className="h-8 w-8 text-black/50" strokeWidth={1.5} />
      <h2 className="mt-5 font-kalice text-3xl font-medium">
        {hasCreations ? `No ${activeFilter.toLowerCase()} creations` : "Your collection is empty"}
      </h2>
      <p className="mt-2 max-w-lg font-sans text-sm leading-relaxed text-muted-foreground">
        {hasCreations
          ? "Choose another filter to return to the rest of your collection."
          : "Your future Antoia experiences and personal pieces will appear here."}
      </p>

      {hasCreations && (
        <Button
          type="button"
          variant="outline"
          className="mt-6 rounded-full border-black/10 bg-transparent font-sans hover:bg-black hover:text-white"
          onClick={onShowAll}
        >
          Show all creations
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default Wallet;